import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '../utils/cn';
import { ThemeContext, } from './theme-context';
const PREFERS_DARK = '(prefers-color-scheme: dark)';
const HOST_THEME_PARAM = 'sec_theme';
function inIframe() {
    try {
        return typeof window !== 'undefined' && window.self !== window.top;
    }
    catch {
        return true;
    }
}
/** Theme aus `?sec_theme=` der eigenen URL (nur light/dark), sonst null. */
export function hostThemeFromUrl(search) {
    try {
        const v = new URLSearchParams(search).get(HOST_THEME_PARAM);
        return v === 'light' || v === 'dark' ? v : null;
    }
    catch {
        return null;
    }
}
/**
 * Theme aus einer Bridge-Nachricht des Hosts (Envelope v1 von 'host',
 * `bridge.theme` oder `bridge.setContext` mit `payload.theme`), sonst null.
 */
export function hostThemeFromMessage(data) {
    if (typeof data !== 'object' || data === null)
        return null;
    const msg = data;
    if (msg.v !== 1 || msg.from !== 'host')
        return null;
    if (msg.type !== 'bridge.theme' && msg.type !== 'bridge.setContext')
        return null;
    const theme = typeof msg.payload === 'object' && msg.payload !== null ? msg.payload.theme : undefined;
    return theme === 'light' || theme === 'dark' ? theme : null;
}
function systemTheme() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }
    return window.matchMedia(PREFERS_DARK).matches ? 'dark' : 'light';
}
/**
 * Wurzel-Provider fuer `@sectepe/ui`. Aktiviert die Design-System-Tokens ueber
 * die `.sec-scope`-Klasse (siehe @sectepe/design-system/components.css),
 * verwaltet Theme- und Sprach-Zustand und stellt beides ueber `useTheme()`
 * bereit. Setzt `data-sec-theme` und `lang` fuer nachgelagerte Komponenten und
 * Fremd-Apps (Guest-Bridge liest dieselben Attribute).
 *
 * Konsument muss zusaetzlich `@sectepe/design-system/index.css` einbinden,
 * damit die `--sec-*`-Tokens und `.sec-*`-Klassen verfuegbar sind.
 */
export function ThemeProvider({ children, defaultTheme = 'system', defaultLanguage = 'de', className, followHost = true, }) {
    const embedded = followHost && inIframe();
    const [theme, setTheme] = useState(() => {
        if (embedded && typeof window !== 'undefined') {
            const fromUrl = hostThemeFromUrl(window.location.search);
            if (fromUrl)
                return fromUrl;
        }
        return defaultTheme;
    });
    const [language, setLanguage] = useState(defaultLanguage);
    const [systemResolved, setSystemResolved] = useState(systemTheme);
    // Auf OS-Praeferenz reagieren, solange `theme === 'system'`.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }
        const media = window.matchMedia(PREFERS_DARK);
        const onChange = () => setSystemResolved(media.matches ? 'dark' : 'light');
        onChange();
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);
    // Eingebettet: Theme-Wechsel der WebUI-Shell live uebernehmen. Angenommen
    // wird nur, was vom Elternfenster kommt (ev.source), und nur das Theme.
    useEffect(() => {
        if (!embedded || typeof window === 'undefined')
            return;
        const onMessage = (ev) => {
            if (ev.source !== window.parent)
                return;
            const next = hostThemeFromMessage(ev.data);
            if (next)
                setTheme(next);
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [embedded]);
    const resolvedTheme = theme === 'system' ? systemResolved : theme;
    // Auch das Dokument selbst themen (html/body, Scrollbalken, Formular-
    // Steuerelemente): data-sec-theme + color-scheme am <html>, damit die
    // Token-Overrides des Design-Systems ausserhalb des .sec-scope greifen und
    // kein weisser Rand/Body im Dark Mode bleibt.
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        const root = document.documentElement;
        root.setAttribute('data-sec-theme', resolvedTheme);
        root.style.colorScheme = resolvedTheme;
    }, [resolvedTheme]);
    const value = useMemo(() => ({
        theme,
        resolvedTheme,
        setTheme,
        language,
        setLanguage,
    }), [theme, resolvedTheme, language]);
    const setThemeStable = useCallback((next) => setTheme(next), []);
    const setLanguageStable = useCallback((next) => setLanguage(next), []);
    return (_jsx(ThemeContext.Provider, { value: { ...value, setTheme: setThemeStable, setLanguage: setLanguageStable }, children: _jsx("div", { className: cn('sec-scope', className), "data-sec-theme": resolvedTheme, lang: language, children: children }) }));
}
